import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@material-ui/core";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import { ModalMuiProps } from "interfaces";
import Labels from "lang/Labels_en";
import { useEffect, useState } from "react";
import EnumDesignation from "utils/enum/EnumDesignation";
import DialogTitle from "../dialog-title/DialogTitle";
import { LongText } from "../long-text";
import { ReorderButtonGroup } from "./ReorderButtonGroup";

export interface FigureReorderDialogProps extends ModalMuiProps {
  article: any;
  onConfirm: any;
}

export const FigureReorderDialog = ({ article, onConfirm, open, onClose }: FigureReorderDialogProps) => {
  const [files, setFiles] = useState(article?.files ?? []);
  const [forceRender, setForceRender] = useState<number>(1);
  const [fileNames, setFileNames] = useState<any>({});

  const swapArrayItems = (array: any, a: any, b: any) => {
    let temp = array[b];
    array[b] = array[a];
    array[a] = temp;
    return array;
  };

  const shiftUpElement: any = (array: any, element: any) => {
    let selectedRowIndex = array.indexOf(element);
    let targetRowIndex = selectedRowIndex - 1;
    if (selectedRowIndex === 0) {
      array = swapArrayItems(array, array.length - 1, 0);
      setFiles(array);
    } else {
      array = swapArrayItems(array, targetRowIndex, selectedRowIndex);
      setFiles(array);
    }
    return array;
  };

  const shiftDownElement = (array: any, element: any) => {
    let selectedRowIndex = array.indexOf(element);
    let targetRowIndex = selectedRowIndex + 1;
    if (selectedRowIndex === array.length - 1) {
      array = swapArrayItems(array, 0, array.length - 1);
    } else {
      array = swapArrayItems(array, targetRowIndex, selectedRowIndex);
    }
    return array;
  };

  const changeFileNames = (fileId: number, value?: string) => {
    setFileNames({
      ...fileNames,
      [fileId]: value,
    });

    const filesNew = files?.map((f: any) => {
      if (f.id === fileId) {
        return {
          ...f,
          figureTitle: value,
        };
      }
      return f;
    });
    setFiles(filesNew);
  };

  useEffect(() => {
    if (article) {
      const figures = files.filter((f: any) => f.type === EnumDesignation.FIGURE);
      const supplementaryFigures = files.filter((f: any) => f.type === EnumDesignation.SUPPLEMENTARY_FIGURE);

      const fileNamesNew: any = {};
      figures?.forEach((f: { id: number; figureTitle: any }, index: number): any => {
        fileNamesNew[f.id] = String(f.figureTitle ?? index + 1);
      });

      supplementaryFigures?.forEach((f: { id: number; figureTitle: any }, index: number): any => {
        fileNamesNew[f.id] = String(f.figureTitle ?? index + 1);
      });

      setFileNames(fileNamesNew);

      const filesNew = files?.map((f: any) => {
        if ([EnumDesignation.FIGURE, EnumDesignation.SUPPLEMENTARY_FIGURE].includes(f.type)) {
          return {
            ...f,
            figureTitle: fileNamesNew[f.id],
          };
        }
        return f;
      });

      setFiles(filesNew);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article]);

  const onSubmit = () => {
    const newFiles = files?.map((f: any) => {
      const newF = f;
      delete newF.id;
      return newF;
    });

    onConfirm({
      ...article,
      files: newFiles?.length > 0 ? newFiles : [],
      fileList: newFiles?.length > 0 ? newFiles : [],
    });
    onClose?.();
  };

  const figures = files.filter((f: any) => f.type === EnumDesignation.FIGURE);
  const supplementaryFigures = files.filter((f: any) => f.type === EnumDesignation.SUPPLEMENTARY_FIGURE);

  return (
    <Dialog maxWidth="lg" open={Boolean(open)} onClose={onClose} aria-labelledby="form-dialog-title">
      <DialogTitle onClose={onClose}>Reorder figures</DialogTitle>
      <DialogContent>
        {figures?.length > 0 && (
          <Box>
            <Typography>{Labels.FIGURE}s</Typography>
            <Divider />
            <List>
              {figures?.map((row: any, index: number) => {
                const preIndex = figures[index - 1]?.id;
                return (
                  <ListItem style={{ padding: 0, margin: 0 }}>
                    <Typography style={{ marginRight: "40px" }}>{`Figure ${fileNames[row.id]}`}</Typography>
                    <ReorderButtonGroup
                      index={index}
                      fileId={row.id}
                      prevValue={index > 0 ? fileNames[preIndex] : undefined}
                      changeFileNames={changeFileNames}
                      selectedValue={fileNames[row.id]}
                    />
                    <IconButton
                      disabled={index === figures?.length - 1}
                      aria-label="down"
                      onClick={(e: any) => {
                        shiftDownElement(files, row);
                        setForceRender(forceRender + 1);
                      }}
                    >
                      <KeyboardArrowDownIcon />
                    </IconButton>
                    <IconButton
                      style={{ marginLeft: "-15px" }}
                      disabled={index === 0}
                      aria-label="up"
                      onClick={(e: any) => {
                        shiftUpElement(files, row);
                        setForceRender(forceRender + 1);
                      }}
                    >
                      <KeyboardArrowUpIcon />
                    </IconButton>
                    <ListItemText primary={<LongText text={row.filename} length={40} />} />
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}

        {supplementaryFigures?.length > 0 && (
          <Box>
            <Typography>{Labels.SUPPLEMENTARY_FIGURE}s</Typography>
            <Divider />
            <List>
              {supplementaryFigures?.map((row: any, index: number) => {
                const preIndex = supplementaryFigures[index - 1]?.id;

                return (
                  <ListItem style={{ padding: 0 }}>
                    <Typography style={{ marginRight: "40px" }}>{`Supplementary figure ${
                      fileNames[row.id]
                    }`}</Typography>
                    <ReorderButtonGroup
                      index={index}
                      fileId={row.id}
                      prevValue={index > 0 ? fileNames[preIndex] : undefined}
                      changeFileNames={changeFileNames}
                      selectedValue={fileNames[row.id]}
                    />
                    <IconButton
                      disabled={index === supplementaryFigures?.length - 1}
                      aria-label="down"
                      onClick={(e: any) => {
                        shiftDownElement(files, row);
                        setForceRender(forceRender + 1);
                      }}
                    >
                      <KeyboardArrowDownIcon />
                    </IconButton>
                    <IconButton
                      style={{ marginLeft: "-15px" }}
                      disabled={index === 0}
                      aria-label="up"
                      onClick={(e: any) => {
                        shiftUpElement(files, row);
                        setForceRender(forceRender + 1);
                      }}
                    >
                      <KeyboardArrowUpIcon />
                    </IconButton>
                    <ListItemText primary={<LongText text={row.filename} length={40} />} />
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {Labels.CANCEL}
        </Button>
        <Button variant="contained" color="primary" onClick={onSubmit}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};
